'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import { useGetWalletInfoQuery, useGetWalletTransactionsQuery } from '@/src/api/rtkQuery/walletApi'
import { WalletTabType, WalletTransaction, DomainBalance } from '@/src/types/wallet'
import { getCookie } from '@/src/utils/browser'
import { getCurrencyByLocale } from '@/src/utils/currency/formatCurrency'

const TRANSACTIONS_PAGE_SIZE = 10

/**
 * Extracts user ID from the userid cookie
 * Cookie format: "email%40domain.com%7C%7C%7C%7C5451900098735%7C"
 * Decoded: "email@domain.com||||5451900098735|"
 * Returns: "5451900098735"
 */
const extractUserId = (userIdCookie: string | null): string | undefined => {
    if (!userIdCookie) return undefined

    try {
        // Decode URL-encoded cookie value
        const decoded = decodeURIComponent(userIdCookie)
        // Extract the numeric userId between pipe characters
        // Pattern: |...|...|...|userId|
        const idMatch = decoded.match(/\|(\d+)\|/)
        return idMatch?.[1]
    } catch {
        // Fallback: try without decoding if already decoded
        const idMatch = userIdCookie.match(/\|(\d+)\|/)
        return idMatch?.[1]
    }
}

export interface UseWalletReturn {
    // Auth state
    isLoggedIn: boolean
    userId: string | number | undefined

    // Wallet balance
    balance: number
    currency: string
    domainBalances: DomainBalance[]
    displayBalance: number
    displayCurrency: string
    hasMultipleDomainBalances: boolean

    // Transactions
    transactions: WalletTransaction[]
    totalRecords: number
    hasMoreTransactions: boolean

    // Tab state
    activeTab: WalletTabType
    setActiveTab: (tab: WalletTabType) => void

    // Pagination
    currentPage: number
    loadMoreTransactions: () => void

    // Loading states
    isLoadingBalance: boolean
    isLoadingTransactions: boolean
    isFetchingTransactions: boolean

    // Error states
    balanceError: string | null
    transactionsError: string | null

    // Actions
    refreshWallet: () => void
}

export const useWallet = (): UseWalletReturn => {
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const isLoggedIn = auth.signedIn

    // Get currency from Redux store (same pattern as other hooks/components)
    const currency = useAppSelector(getSelectedCurrency)

    // Extract userId directly from cookies (same pattern as authSlice.ts)
    const userid = useMemo(() => {
        if (typeof window === 'undefined') return null
        return getCookie('userid')
    }, [])
    const userId = useMemo(() => {
        return userid ? extractUserId(userid) : undefined
    }, [userid])

    // Ensure auth state is refreshed on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            dispatch(refreshAuthUser())
        }
    }, [dispatch])

    // Tab state
    const [activeTab, setActiveTabState] = useState<WalletTabType>('EARNED')
    const [currentPage, setCurrentPage] = useState(1)
    const [accumulatedTransactions, setAccumulatedTransactions] = useState<WalletTransaction[]>([])

    // Get domain currency for display preference (used to find matching balance from domainBalances)
    const domainCurrency = useMemo(() => {
        if (typeof window === 'undefined') {
            return getCurrencyByLocale().currency
        }
        return getCurrencyByLocale().currency
    }, [])

    // Skip queries if not logged in or userId is invalid
    const shouldSkip = !isLoggedIn

    // Fetch wallet info
    const {
        data: walletInfo,
        isLoading: isLoadingBalance,
        error: balanceErrorObj,
        refetch: refetchWalletInfo
    } = useGetWalletInfoQuery({ userId: userId as string | number, currency }, { skip: shouldSkip })

    // Fetch transactions - query re-runs automatically when params change
    const {
        data: transactionsData,
        isLoading: isLoadingTransactions,
        isFetching: isFetchingTransactions,
        error: transactionsErrorObj,
        refetch: refetchTransactions
    } = useGetWalletTransactionsQuery(
        {
            userId: userId as string | number,
            currency,
            type: activeTab,
            offset: currentPage,
            size: TRANSACTIONS_PAGE_SIZE
        },
        { skip: shouldSkip }
    )

    // Parse balance data
    const balance = walletInfo?.status === 'SUCCESS' ? (walletInfo.balance ?? 0) : 0
    const walletCurrency = walletInfo?.status === 'SUCCESS' ? (walletInfo.currency ?? currency) : currency
    const domainBalances = walletInfo?.status === 'SUCCESS' ? (walletInfo.domainBalances ?? []) : []

    // Calculate display balance (prefer domain balance if available)
    const { displayBalance, displayCurrency } = useMemo(() => {
        const domainMatch = domainBalances.find(db => db.currency === domainCurrency)
        if (domainMatch) {
            return { displayBalance: domainMatch.balance, displayCurrency: domainMatch.currency }
        }
        return { displayBalance: balance, displayCurrency: walletCurrency }
    }, [balance, walletCurrency, domainBalances, domainCurrency])

    const hasMultipleDomainBalances = domainBalances.length > 1

    // Parse transactions - use accumulated for pagination
    const transactions = useMemo(() => {
        if (!transactionsData?.transactions) return accumulatedTransactions
        // For page 1, return fresh data; for other pages, accumulate
        if (currentPage === 1) {
            return transactionsData.transactions
        }
        // Combine accumulated with new data, avoiding duplicates
        const existingIds = new Set(accumulatedTransactions.map(t => t.transactionId))
        const newTransactions = transactionsData.transactions.filter(t => !existingIds.has(t.transactionId))
        return [...accumulatedTransactions, ...newTransactions]
    }, [transactionsData?.transactions, currentPage, accumulatedTransactions])

    const totalRecords = transactionsData?.totalRecords ?? 0
    const hasMoreTransactions = transactions.length < totalRecords

    // Error messages
    const balanceError = balanceErrorObj
        ? 'Unable to fetch wallet balance'
        : walletInfo?.status === 'FAILURE'
          ? walletInfo.message || 'Failed to load wallet'
          : null

    const transactionsError = transactionsErrorObj ? 'Unable to fetch transactions' : null

    // Handle tab change - reset pagination
    const setActiveTab = useCallback((tab: WalletTabType) => {
        setActiveTabState(tab)
        setCurrentPage(1)
        setAccumulatedTransactions([])
    }, [])

    // Load more transactions
    const loadMoreTransactions = useCallback(() => {
        if (isFetchingTransactions || transactions.length >= totalRecords) return
        // Save current transactions before loading more
        setAccumulatedTransactions(transactions)
        setCurrentPage(prev => prev + 1)
    }, [isFetchingTransactions, transactions, totalRecords])

    // Refresh wallet data
    const refreshWallet = useCallback(() => {
        refetchWalletInfo()
        setCurrentPage(1)
        setAccumulatedTransactions([])
        refetchTransactions()
    }, [refetchWalletInfo, refetchTransactions])

    return {
        isLoggedIn,
        userId,
        balance,
        currency: walletCurrency,
        domainBalances,
        displayBalance,
        displayCurrency,
        hasMultipleDomainBalances,
        transactions,
        totalRecords,
        hasMoreTransactions,
        activeTab,
        setActiveTab,
        currentPage,
        loadMoreTransactions,
        isLoadingBalance,
        isLoadingTransactions,
        isFetchingTransactions,
        balanceError,
        transactionsError,
        refreshWallet
    }
}

export default useWallet
